import { ReactElement, useEffect, useState } from 'react';
import cx from 'clsx';
import { useField, useForm } from 'react-final-form';
import { Box, DatePicker, FieldErrorTooltip, Input, useSafeId } from '@ff-it/ui';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, isSameDay } from 'date-fns';
import { formatDate } from 'utilities';
import { FilterChip } from './FilterChip';
import { dateCalendar, fullPicker } from './dateChip.css';
import { customCalendar, pickerTabs } from './DateRangeFilterChip.css';
import { INPUT_FORMAT, fmtInput, parseInput, toDate, toISO } from './dateChip';

type DateRangeFilterChipProps = {
  label: string;
  fromName: string;
  toName: string;
  minDate?: Date;
  maxDate?: Date;
  defaultFrom?: string;
  defaultTo?: string;
};

type Mode = 'custom' | 'year' | 'month';

const deriveMode = (from: Date | null, to: Date | null): Mode => {
  if (from && to) {
    if (isSameDay(from, startOfYear(from)) && isSameDay(to, endOfYear(from))) {
      return 'year';
    }
    if (isSameDay(from, startOfMonth(from)) && isSameDay(to, endOfMonth(from))) {
      return 'month';
    }
  }
  return 'custom';
};

export function DateRangeFilterChip({
  label,
  fromName,
  toName,
  minDate,
  maxDate,
  defaultFrom,
  defaultTo,
}: DateRangeFilterChipProps): ReactElement {
  const fromErrorId = useSafeId();
  const toErrorId = useSafeId();
  const form = useForm();
  const {
    input: { value: fromValue },
  } = useField<string | undefined>(fromName, { subscription: { value: true } });
  const {
    input: { value: toValue },
  } = useField<string | undefined>(toName, { subscription: { value: true } });
  const hasValue = !!(fromValue && toValue);
  const atDefault = defaultFrom != null && defaultTo != null && fromValue === defaultFrom && toValue === defaultTo;

  // open on the tab matching the current range
  const [mode, setMode] = useState<Mode>(() => deriveMode(toDate(fromValue), toDate(toValue)));

  // local draft for the in-progress range; plus editable text for manual entry
  const [draft, setDraft] = useState<[Date | null, Date | null]>([toDate(fromValue), toDate(toValue)]);
  const [text, setText] = useState<[string, string]>([fmtInput(draft[0]), fmtInput(draft[1])]);
  // only surface the validation error after blur, not mid-typing
  const [showError, setShowError] = useState(false);
  useEffect(() => setText([fmtInput(draft[0]), fmtInput(draft[1])]), [draft]);
  // resync the draft when the value changes externally (urlSync back/forward, programmatic reset)
  useEffect(() => {
    setDraft([toDate(fromValue), toDate(toValue)]);
  }, [fromValue, toValue]);

  const pf = parseInput(text[0]);
  const pt = parseInput(text[1]);
  const fromInvalid = !!text[0].trim() && !pf;
  const toInvalid = !!text[1].trim() && !pt;
  const orderInvalid = !!(pf && pt && pf > pt);
  // per-field error message (shown under each input, after blur)
  const fieldMsg = (invalid: boolean): string | null =>
    showError ? (invalid ? 'Invalid date' : orderInvalid ? 'Invalid period' : null) : null;
  const fromMsg = fieldMsg(fromInvalid);
  const toMsg = fieldMsg(toInvalid);

  const commit = (range: [Date | null, Date | null]): void => {
    setDraft(range);
    if (range[0] && range[1] && range[0] <= range[1]) {
      form.change(fromName, toISO(range[0]));
      form.change(toName, toISO(range[1]));
    }
  };

  const clear = (): void => {
    setDraft([toDate(defaultFrom), toDate(defaultTo)]);
    form.batch(() => {
      form.change(fromName, defaultFrom ?? undefined);
      form.change(toName, defaultTo ?? undefined);
    });
  };

  const applyText = (): void => {
    if (pf && pt && pf <= pt) {
      commit([pf, pt]);
    }
  };

  const tab = (m: Mode, title: string): ReactElement => (
    <li className="nav-item">
      <button type="button" className={cx('nav-link', mode === m && 'active')} onClick={() => setMode(m)}>
        {title}
      </button>
    </li>
  );

  return (
    <FilterChip
      label={label}
      active={hasValue && !atDefault}
      valueNode={hasValue ? `${formatDate(fromValue as string)} – ${formatDate(toValue as string)}` : null}
      onClear={clear}
      onClose={applyText}
      width={null}
      flush
    >
      {(close) => (
        <Box>
          <ul className={cx('nav nav-tabs px-2 pt-2', pickerTabs)}>
            {tab('custom', 'Custom')}
            {tab('year', 'Year')}
            {tab('month', 'Month')}
          </ul>
          {mode === 'custom' && (
            <div className="d-flex align-items-center px-2 pt-2 mb-2">
              <div className="position-relative flex-fill">
                <Input
                  size="sm"
                  valid={showError && (fromInvalid || orderInvalid) ? false : undefined}
                  aria-describedby={fromMsg ? fromErrorId : undefined}
                  placeholder={INPUT_FORMAT}
                  value={text[0]}
                  onChange={(e) => {
                    setText([e.target.value, text[1]]);
                    setShowError(false);
                  }}
                  onBlur={() => {
                    applyText();
                    setShowError(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyText();
                      close();
                    }
                  }}
                />
                {fromMsg && <FieldErrorTooltip id={fromErrorId}>{fromMsg}</FieldErrorTooltip>}
              </div>
              <span className="mx-1">–</span>
              <div className="position-relative flex-fill">
                <Input
                  size="sm"
                  valid={showError && (toInvalid || orderInvalid) ? false : undefined}
                  aria-describedby={toMsg ? toErrorId : undefined}
                  placeholder={INPUT_FORMAT}
                  value={text[1]}
                  onChange={(e) => {
                    setText([text[0], e.target.value]);
                    setShowError(false);
                  }}
                  onBlur={() => {
                    applyText();
                    setShowError(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyText();
                      close();
                    }
                  }}
                />
                {toMsg && <FieldErrorTooltip id={toErrorId}>{toMsg}</FieldErrorTooltip>}
              </div>
            </div>
          )}
          <Box className={cx(dateCalendar, mode === 'custom' ? customCalendar : fullPicker)}>
            {mode === 'custom' && (
              <DatePicker
                selectsRange
                inline
                monthsShown={2}
                startDate={draft[0]}
                endDate={draft[1]}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(range: [Date | null, Date | null]) => commit(range)}
              />
            )}
            {mode === 'year' && (
              <DatePicker
                showYearPicker
                inline
                selected={toDate(fromValue)}
                minDate={minDate}
                maxDate={maxDate}
                allowSameDay
                onChange={(d: Date | null) => d && commit([startOfYear(d), endOfYear(d)])}
              />
            )}
            {mode === 'month' && (
              <DatePicker
                showMonthYearPicker
                inline
                selected={toDate(fromValue)}
                minDate={minDate}
                maxDate={maxDate}
                allowSameDay
                onChange={(d: Date | null) => d && commit([startOfMonth(d), endOfMonth(d)])}
              />
            )}
          </Box>
        </Box>
      )}
    </FilterChip>
  );
}
